import React from "react";
import { AlertTriangle } from "lucide-react";

// No pass_percent — Behavioural has no pass/fail threshold in Phase 1 (proposal §6,
// "nothing in the PRD gates on a cutoff, only 'a score gets computed'").
// allow_back_navigation/allow_skip are saved but not yet wired into a builder control
// or read by BehavioralTestPage.jsx (Prev/Skip are gated purely by question position
// today) — same deferred-but-documented status as MCQ's shuffle_questions/shuffle_options.
// See documentation/behavioral-assessment/behavioral-test-phase1-proposal.md.
export const DEFAULT_CONFIG = {
  time_limit_minutes: 30,
  allow_back_navigation: true,
  allow_skip: true,
};

const inputCls =
  "w-full px-6 py-3 mt-2 bg-transparent border border-[#5A4479] rounded-full dark:text-[#CCCCCC] text-textSecondary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200";
const labelCls =
  "absolute text-sm leading-none font-normal dark:text-[#DAC0FF] text-textSecondary -top-2.5 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1.5 py-0.5";

const SetDetailsCard = ({ draft, nameWarning, onDraftChange, locked = false }) => {
  if (!draft) return null;

  const setField = (field, value) => onDraftChange({ ...draft, [field]: value });
  const setConfig = (field, value) =>
    onDraftChange({ ...draft, config: { ...draft.config, [field]: value } });
  // readOnly, not disabled — backend already rejects a locked set's save, so this
  // only needs to signal "can't edit," not block selection/copy or screen readers.
  const fieldCls = `${inputCls} ${locked ? "opacity-60 cursor-default" : ""}`;

  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-start">
      <div className="w-full sm:w-56 shrink-0 relative">
        <label className={labelCls}>
          Set Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder="e.g. People Manager Round"
          maxLength={45}
          readOnly={locked}
          className={fieldCls}
        />
        {nameWarning && (
          <p className="flex items-center gap-1.5 text-xs text-amber-400 mt-1.5">
            <AlertTriangle size={11} className="shrink-0" /> {nameWarning}
          </p>
        )}
      </div>

      <div className="flex-1 w-full min-w-[10rem] sm:max-w relative">
        <label className={labelCls}>
          Description{" "}
          <span className="text-[10px] font-normal dark:text-gray-500 text-textSecondary">
            (optional)
          </span>
        </label>
        <input
          type="text"
          value={draft.description || ""}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="What this test covers — e.g. Standard behavioural screen for people-manager roles"
          maxLength={300}
          readOnly={locked}
          className={fieldCls}
        />
      </div>

      <div className="w-full sm:w-44 shrink-0 relative">
        <label className={labelCls}>Time limit (min)</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft.config.time_limit_minutes ?? ""}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "");
            setConfig("time_limit_minutes", Math.min(300, parseInt(digits, 10) || 0));
          }}
          readOnly={locked}
          className={fieldCls}
        />
      </div>
    </div>
  );
};

export default SetDetailsCard;
